import { BaseComponent } from '../../lib/BaseComponent.js';

/**
 * Install Prompt Component
 * Shows a prompt to install the PWA on supported devices
 */
export class InstallPrompt extends BaseComponent {
  private deferredPrompt: any = null;
  private isDismissed = false;

  connectedCallback(): void {
    super.connectedCallback();

    // Check if already dismissed
    this.isDismissed = localStorage.getItem('pwa-install-dismissed') === 'true';

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      this.isDismissed = true;
    }

    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e;

      if (!this.isDismissed) {
        this.shadow.innerHTML = '';
        this.render();
      }
    });

    // Listen for appinstalled event
    window.addEventListener('appinstalled', () => {
      console.log('PWA installed successfully');
      this.deferredPrompt = null;
      this.shadow.innerHTML = '';
    });
  }

  protected async render(): Promise<void> {
    if (!this.deferredPrompt || this.isDismissed) {
      return;
    }

    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    const container = this.createElement('div', { className: 'install-prompt' });

    // Content
    const content = this.createElement('div', { className: 'prompt-content' });

    const icon = this.createElement('div', { className: 'prompt-icon' }, '📱');
    content.appendChild(icon);

    const textContainer = this.createElement('div', { className: 'prompt-text' });
    const title = this.createElement('h3', {}, 'Install FireGuard');
    const description = this.createElement('p', {}, 'Install the app on your device for quick access and offline support');
    textContainer.appendChild(title);
    textContainer.appendChild(description);
    content.appendChild(textContainer);

    container.appendChild(content);

    // Actions
    const actions = this.createElement('div', { className: 'prompt-actions' });

    const dismissBtn = this.createElement('button', { className: 'btn-dismiss' }, 'Not Now');
    dismissBtn.addEventListener('click', () => this.handleDismiss());
    actions.appendChild(dismissBtn);

    const installBtn = this.createElement('button', { className: 'btn-install' }, 'Install');
    installBtn.addEventListener('click', () => this.handleInstall());
    actions.appendChild(installBtn);

    container.appendChild(actions);

    // Close button
    const closeBtn = this.createElement('button', { className: 'close-btn' }, '×');
    closeBtn.addEventListener('click', () => this.handleDismiss());
    container.appendChild(closeBtn);

    this.shadow.appendChild(container);
  }

  private handleDismiss(): void {
    this.isDismissed = true;
    localStorage.setItem('pwa-install-dismissed', 'true');
    this.shadow.innerHTML = '';
  }

  private async handleInstall(): Promise<void> {
    if (!this.deferredPrompt) {
      return;
    }

    // Show install prompt
    this.deferredPrompt.prompt();

    // Wait for user response
    const { outcome } = await this.deferredPrompt.userChoice;
    console.log(`User response to install prompt: ${outcome}`);

    // Clear deferred prompt
    this.deferredPrompt = null;

    // Hide prompt
    this.shadow.innerHTML = '';
  }

  private getStyles(): string {
    return `
      .install-prompt {
        position: fixed;
        bottom: 20px;
        left: 20px;
        right: 20px;
        max-width: 480px;
        margin: 0 auto;
        background: white;
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
        padding: var(--spacing-lg);
        z-index: 9998;
        animation: slideUp 0.3s ease-out;
        border: 1px solid var(--gray-200);
      }

      @keyframes slideUp {
        from {
          transform: translateY(100px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      .prompt-content {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        margin-bottom: var(--spacing-md);
      }

      .prompt-icon {
        font-size: 48px;
        flex-shrink: 0;
      }

      .prompt-text {
        flex: 1;
      }

      .prompt-text h3 {
        font-size: 18px;
        font-weight: 600;
        color: var(--gray-900);
        margin: 0 0 4px 0;
      }

      .prompt-text p {
        font-size: 14px;
        color: var(--gray-600);
        margin: 0;
        line-height: 1.4;
      }

      .prompt-actions {
        display: flex;
        gap: var(--spacing-md);
      }

      .btn-dismiss,
      .btn-install {
        flex: 1;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        border: none;
      }

      .btn-dismiss {
        background: white;
        color: var(--gray-700);
        border: 1px solid var(--gray-300);
      }

      .btn-dismiss:hover {
        background: var(--gray-50);
      }

      .btn-install {
        background: var(--primary);
        color: white;
      }

      .btn-install:hover {
        background: var(--primary-dark);
      }

      .close-btn {
        position: absolute;
        top: 12px;
        right: 12px;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: transparent;
        border: none;
        color: var(--gray-400);
        font-size: 24px;
        line-height: 1;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .close-btn:hover {
        background: var(--gray-100);
        color: var(--gray-600);
      }

      @media (max-width: 768px) {
        .install-prompt {
          bottom: 0;
          left: 0;
          right: 0;
          border-radius: 12px 12px 0 0;
          max-width: 100%;
        }

        .prompt-content {
          flex-direction: column;
          text-align: center;
        }

        .prompt-text h3 {
          font-size: 16px;
        }

        .prompt-text p {
          font-size: 13px;
        }

        .prompt-actions {
          flex-direction: column;
        }
      }

      @media (display-mode: standalone) {
        .install-prompt {
          display: none;
        }
      }
    `;
  }
}

// Register custom element
customElements.define('install-prompt', InstallPrompt);
